import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import type { QueryClient } from '@tanstack/react-query'
import type { ToastSeverity } from '@/shared/toast/ToastProvider'
import { canProceedToNext } from './survey-answer'
import { saveAndAdvanceSurvey } from './survey-execution-handlers'
import { notifySurveySessionTimedOut } from './handle-survey-session-blocked'
import { resolveSurveySaveFailure } from './survey-save-error'
import type { QuestionRoutingRules } from '@/modules/routing'
import type { SurveyDefinition, SurveyDraftAnswer, SurveyQuestion } from './survey-question.types'
import type {
  ProviderInstrumentInfo,
  ProviderReward,
  ProviderSubmitConfirmation,
} from './types'

type ShowToast = (message: string, severity?: ToastSeverity, autoHideDuration?: number) => void

interface UseSurveyExecutionNextParams {
  instrumentId: string | undefined
  responseId: string | undefined
  currentQuestion: SurveyQuestion | null
  definition: SurveyDefinition | null
  draftAnswer: SurveyDraftAnswer
  answerMap: Map<string, SurveyDraftAnswer>
  routingRules: QuestionRoutingRules[]
  visitedQuestionIds: string[]
  questionEnteredAt: number
  session: { definition?: unknown; response?: unknown } | undefined
  info: ProviderInstrumentInfo | undefined
  saveMutation: Parameters<typeof saveAndAdvanceSurvey>[0]['saveMutation']
  submitMutation: Parameters<typeof saveAndAdvanceSurvey>[0]['submitMutation']
  queryClient: QueryClient
  showToast: ShowToast
  navigate: NavigateFunction
  timeoutHandledRef: MutableRefObject<boolean>
  setAnswerMap: Dispatch<SetStateAction<Map<string, SurveyDraftAnswer>>>
  setLocalQuestionId: Dispatch<SetStateAction<string | null>>
  setVisitedQuestionIds: Dispatch<SetStateAction<string[]>>
  setSubmitConfirmation: Dispatch<SetStateAction<ProviderSubmitConfirmation | null>>
  setCompletionReward: Dispatch<SetStateAction<ProviderReward | null>>
  setSubmitSuccessModalOpen: Dispatch<SetStateAction<boolean>>
  setShowRequiredError: Dispatch<SetStateAction<boolean>>
  setTargetBlocked: Dispatch<SetStateAction<boolean>>
  handleSessionFailure: (failure: ReturnType<typeof resolveSurveySaveFailure>) => void
}

export function useSurveyExecutionNext({
  instrumentId,
  responseId,
  currentQuestion,
  definition,
  draftAnswer,
  answerMap,
  routingRules,
  visitedQuestionIds,
  questionEnteredAt,
  session,
  info,
  saveMutation,
  submitMutation,
  queryClient,
  showToast,
  navigate,
  timeoutHandledRef,
  setAnswerMap,
  setLocalQuestionId,
  setVisitedQuestionIds,
  setSubmitConfirmation,
  setCompletionReward,
  setSubmitSuccessModalOpen,
  setShowRequiredError,
  setTargetBlocked,
  handleSessionFailure,
}: UseSurveyExecutionNextParams) {
  return useCallback(async () => {
    if (!instrumentId || !responseId || !currentQuestion || !definition) return

    if (!canProceedToNext(currentQuestion, draftAnswer)) {
      setShowRequiredError(true)
      return
    }
    setShowRequiredError(false)

    try {
      await saveAndAdvanceSurvey({
        instrumentId,
        responseId,
        currentQuestion,
        definition,
        routingRules,
        draftAnswer,
        answerMap,
        questionEnteredAt,
        saveMutation,
        submitMutation,
        queryClient,
        session,
        visitedQuestionIds,
        info,
        showToast,
        setAnswerMap,
        setLocalQuestionId,
        setVisitedQuestionIds,
        setSubmitConfirmation,
        setCompletionReward,
        setSubmitSuccessModalOpen,
        onSessionBlocked: (reason, timeoutMessage) => {
          if (reason === 'timeout') {
            if (timeoutHandledRef.current) return
            timeoutHandledRef.current = true
            notifySurveySessionTimedOut(showToast, navigate, timeoutMessage)
            return
          }
          setTargetBlocked(true)
        },
      })
    } catch (err) {
      const failure = resolveSurveySaveFailure(err)
      if (failure.kind === 'inline_required') {
        setShowRequiredError(true)
        return
      }
      if (failure.kind === 'session_timeout' || failure.kind === 'session_target_blocked') {
        handleSessionFailure(failure)
        return
      }
      showToast(failure.message, 'error')
    }
  }, [
    instrumentId,
    responseId,
    currentQuestion,
    definition,
    draftAnswer,
    answerMap,
    routingRules,
    visitedQuestionIds,
    questionEnteredAt,
    session,
    info,
    saveMutation,
    submitMutation,
    queryClient,
    showToast,
    navigate,
    timeoutHandledRef,
    setAnswerMap,
    setLocalQuestionId,
    setVisitedQuestionIds,
    setSubmitConfirmation,
    setCompletionReward,
    setSubmitSuccessModalOpen,
    setShowRequiredError,
    setTargetBlocked,
    handleSessionFailure,
  ])
}
